import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** A self-contained product selector. No socks offers or global variant overrides. */
class SkinEliteLeggings {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.avl-leggings__form');
    this.variantInput = section.querySelector('[data-variant-id]');
    this.quantityInput = section.querySelector('[data-quantity]');
    this.variants = JSON.parse(section.querySelector('[data-variants]').textContent);
    this.gallerySets = JSON.parse(section.querySelector('[data-gallery-sets]').textContent);
    this.galleryColour = section.dataset.galleryColour;
    this.selectedVariant = this.variants.find((variant) => String(variant.id) === this.variantInput.value);
    if (!this.form || !this.selectedVariant) return;
    this.busy = false;
    this.events = new AbortController();
    this.currency = section.dataset.currency || 'USD';
    try {
      this.moneyFormatter = new Intl.NumberFormat(section.dataset.locale || 'en', {
        style: 'currency',
        currency: this.currency,
      });
    } catch {
      this.moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
    this.bindEvents();
    this.bindGalleryImageStates();
    this.bindZoom();
    this.updateSelection(false);
    section.querySelector('[data-variant-controls]').hidden = false;
    section.querySelector('[data-variant-fallback]').hidden = true;
    section.dataset.leggingsInitialized = 'true';
    this.observeGallery();
    this.setupHighlights();
    this.observeAddButton();
  }

  listen(element, type, callback) {
    element?.addEventListener(type, callback, { signal: this.events.signal });
  }

  bindEvents() {
    this.listen(this.form, 'submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });
    this.section.querySelectorAll('[data-colour], [data-size]').forEach((input) => {
      this.listen(input, 'change', () => this.updateSelection(true));
    });
    this.listen(this.quantityInput, 'input', () => this.updateQuantity(false));
    this.listen(this.quantityInput, 'change', () => this.updateQuantity());
    this.section.querySelectorAll('[data-quantity-change]').forEach((button) => {
      this.listen(button, 'click', () => {
        this.quantityInput.value = this.quantity + Number(button.dataset.quantityChange);
        this.updateQuantity();
      });
    });
    const dialog = this.section.querySelector('[data-size-dialog]');
    this.section.querySelectorAll('[data-size-open]').forEach((button) => {
      this.listen(button, 'click', () => {
        this.dialogReturnFocus = button;
        dialog.showModal();
      });
    });
    this.listen(this.section.querySelector('[data-size-close]'), 'click', () => dialog.close());
    this.listen(dialog, 'click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) {
        dialog.close();
      }
    });
    this.listen(dialog, 'close', () => this.dialogReturnFocus?.focus({ preventScroll: true }));
    this.listen(window, 'popstate', () => {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      const variant = variantId
        ? this.variants.find((item) => String(item.id) === variantId)
        : this.variants.find((item) => item.available) || this.variants[0];
      if (!variant) return;
      this.section.querySelectorAll('[data-colour]').forEach((input) => { input.checked = input.value === variant.option1; });
      this.section.querySelectorAll('[data-size]').forEach((input) => { input.checked = input.value === variant.option2; });
      this.updateSelection(false);
    });
  }

  get quantity() {
    const parsed = Number.parseInt(this.quantityInput.value, 10);
    return Number.isFinite(parsed) ? Math.min(99, Math.max(1, parsed)) : 1;
  }

  formatMoney(cents, includeCurrency = true) {
    return `${this.moneyFormatter.format(Number(cents) / 100)}${includeCurrency ? ` ${this.currency}` : ''}`;
  }

  colourLabel(colour) {
    return colour.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
  }

  updateQuantity(normalize = true) {
    if (normalize) this.quantityInput.value = this.quantity;
    this.section.querySelector('[data-sticky-quantity]').textContent = `Qty ${this.quantity}`;
  }

  updateSelection(updateUrl) {
    const colour = this.section.querySelector('[data-colour]:checked')?.value;
    const size = this.section.querySelector('[data-size]:checked')?.value;
    const nextVariant = this.variants.find((variant) => variant.option1 === colour && variant.option2 === size);
    this.selectedVariant = nextVariant;
    this.section.querySelectorAll('[data-size]').forEach((input) => {
      const variant = this.variants.find((item) => item.option1 === colour && item.option2 === input.value);
      input.disabled = !variant?.available;
    });
    this.section.querySelector('[data-colour-label]').textContent = this.colourLabel(colour || '');
    this.section.querySelector('[data-size-label]').textContent = size || '';
    this.section.querySelector('[data-sticky-variant]').textContent = `${this.colourLabel(colour || '')} / ${size || ''}`;
    const stock = this.section.querySelector('[data-stock]');
    stock.textContent = nextVariant?.available ? 'In stock' : 'Sold out';
    stock.classList.toggle('is-unavailable', !nextVariant?.available);
    this.setButtonState();
    this.setStatus('');
    if (!nextVariant) return;
    this.variantInput.value = String(nextVariant.id);
    this.section.querySelectorAll('[data-price]').forEach((price) => {
      price.textContent = this.formatMoney(nextVariant.price);
    });
    const hasSaving = Number(nextVariant.compare_at_price) > Number(nextVariant.price);
    const comparison = this.section.querySelector('[data-compare]');
    const saving = this.section.querySelector('[data-saving]');
    comparison.hidden = !hasSaving;
    saving.hidden = !hasSaving;
    comparison.textContent = hasSaving ? this.formatMoney(nextVariant.compare_at_price, false) : '';
    saving.textContent = hasSaving ? `Save ${this.formatMoney(nextVariant.compare_at_price - nextVariant.price, false)}` : '';
    this.updateGallery(nextVariant);
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(nextVariant.id));
      window.history.replaceState({}, '', url);
    }
    this.updateQuantity();
  }

  setButtonState() {
    this.section.querySelectorAll('[data-add-button]').forEach((button) => {
      button.disabled = this.busy || !this.selectedVariant?.available;
      button.setAttribute('aria-busy', String(this.busy));
      button.textContent = this.busy ? 'Adding…' : this.selectedVariant?.available ? 'Add to cart' : 'Sold out';
    });
  }

  setStatus(message, isError = false) {
    const status = this.section.querySelector('[data-status]');
    status.textContent = message;
    status.classList.toggle('is-error', isError);
    status.setAttribute('role', isError ? 'alert' : 'status');
    const stickyStatus = this.section.querySelector('[data-sticky-status]');
    stickyStatus.textContent = isError ? message : '';
    stickyStatus.hidden = !isError;
  }

  imageUrl(src) {
    return new URL(src, document.baseURI || window.location.href).href;
  }

  imageAtWidth(src, width) {
    const url = new URL(src, document.baseURI || window.location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  }

  revealLoadedImage(image) {
    const intendedSrc = image.dataset.intendedSrc;
    if (!intendedSrc || !image.complete || !image.naturalWidth) return;
    if (this.imageUrl(image.currentSrc || image.src) !== this.imageUrl(intendedSrc)) return;
    image.dataset.loading = 'false';
  }

  setGalleryImage(image, src, alt) {
    if (!image || !src) return;
    image.alt = alt;
    image.dataset.intendedSrc = src;
    if (image.getAttribute('src') !== src) {
      // Hide the previous colour until this exact requested source has loaded.
      image.dataset.loading = 'true';
      image.removeAttribute('srcset');
      image.src = src;
    }
    this.revealLoadedImage(image);
  }

  bindGalleryImageStates() {
    const images = [
      ...this.section.querySelectorAll('[data-gallery-image]'),
      this.section.querySelector('[data-sticky-image]'),
      this.section.querySelector('[data-zoom-image]'),
    ].filter(Boolean);
    images.forEach((image) => {
      this.listen(image, 'load', () => this.revealLoadedImage(image));
      this.listen(image, 'error', () => {
        if (image.complete && !image.naturalWidth) image.dataset.loading = 'false';
      });
    });
  }

  updateGallery(variant) {
    const gallerySet = this.gallerySets[variant.option1];
    if (!gallerySet) return;
    const colourChanged = this.galleryColour !== variant.option1;
    const variantImage = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
    const variantSources = gallerySet.variantImages?.[String(variant.id)];
    // Liquid supplies the exact SSR URLs, including width and CDN host. Do not
    // replace an already loaded hero/thumbnail with an unresized image on hydration.
    const heroSrc = variantSources?.hero || (variantImage ? this.imageAtWidth(variantImage, 1200) : gallerySet.frontFallback);
    const thumbnailSrc = variantSources?.thumbnail || (variantImage ? this.imageAtWidth(variantImage, 150) : gallerySet.frontFallback);
    const images = this.section.querySelectorAll('[data-gallery-image]');
    images.forEach((image, index) => {
      const slide = gallerySet.slides[index];
      if (!slide) return;
      this.setGalleryImage(image, index === 0 ? heroSrc : slide.src, slide.alt);
      image.closest('[data-zoom-open]')?.setAttribute('aria-label', `Enlarge ${slide.alt}`);
    });
    this.section.querySelectorAll('[data-gallery-label]').forEach((label) => {
      label.textContent = this.colourLabel(variant.option1);
    });
    this.setGalleryImage(this.section.querySelector('[data-sticky-image]'), thumbnailSrc, gallerySet.slides[0].alt);
    this.galleryColour = variant.option1;
    this.section.dataset.galleryColour = variant.option1;
    if (colourChanged) {
      this.section.querySelector('[data-gallery]').scrollTo({ left: 0, behavior: 'auto' });
      this.updateGalleryDots(0);
    }
    if (this.section.querySelector('[data-zoom-dialog]')?.open) {
      const image = this.zoomReturnFocus?.querySelector('img');
      if (image) this.setGalleryImage(this.section.querySelector('[data-zoom-image]'), image.dataset.intendedSrc || image.getAttribute('src'), image.alt);
    }
  }

  observeGallery() {
    const gallery = this.section.querySelector('[data-gallery]');
    const slides = Array.from(gallery.children);
    const dots = this.section.querySelectorAll('[data-slide]');
    dots.forEach((dot) => {
      this.listen(dot, 'click', () => {
        const slide = slides[Number(dot.dataset.slide)];
        gallery.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
    });
    this.listen(gallery, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      gallery.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * gallery.clientWidth, behavior: 'smooth' });
    });
    this.listen(gallery, 'scroll', () => this.updateGalleryDots());
    this.listen(window, 'resize', () => this.updateGalleryDots());
    this.updateGalleryDots();
  }

  updateGalleryDots(forcedIndex) {
    const gallery = this.section.querySelector('[data-gallery]');
    const dots = this.section.querySelectorAll('[data-slide]');
    const visibleIndex = forcedIndex ?? Math.round(gallery.scrollLeft / (gallery.clientWidth || 1));
    const index = Math.max(0, Math.min(dots.length - 1, visibleIndex));
    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', index === dotIndex);
      dot.setAttribute('aria-current', String(index === dotIndex));
    });
  }

  getHighlightIndex() {
    const track = this.section.querySelector('[data-highlights-track]');
    const cards = Array.from(track.children);
    const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
    return Math.max(0, Math.min(cards.length - 1, Math.round(track.scrollLeft / (step || 1))));
  }

  updateHighlightControls() {
    const track = this.section.querySelector('[data-highlights-track]');
    const count = track.children.length;
    const index = this.getHighlightIndex();
    const counter = this.section.querySelector('[data-highlight-counter]');
    const counterText = `${index + 1} / ${count}`;
    if (counter.textContent !== counterText) counter.textContent = counterText;
    this.section.querySelector('[data-highlight-previous]').disabled = index === 0;
    this.section.querySelector('[data-highlight-next]').disabled = index === count - 1;
  }

  moveHighlight(direction) {
    const track = this.section.querySelector('[data-highlights-track]');
    const cards = Array.from(track.children);
    const index = Math.max(0, Math.min(cards.length - 1, this.getHighlightIndex() + direction));
    track.scrollTo({
      left: cards[index].offsetLeft - cards[0].offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  setupHighlights() {
    const track = this.section.querySelector('[data-highlights-track]');
    if (!track) return;
    this.listen(this.section.querySelector('[data-highlight-previous]'), 'click', () => this.moveHighlight(-1));
    this.listen(this.section.querySelector('[data-highlight-next]'), 'click', () => this.moveHighlight(1));
    this.listen(track, 'keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      this.moveHighlight(event.key === 'ArrowRight' ? 1 : -1);
    });
    this.listen(track, 'scroll', () => this.updateHighlightControls());
    this.listen(window, 'resize', () => this.updateHighlightControls());
    this.section.querySelector('[data-highlights-controls]').hidden = false;
    this.updateHighlightControls();
  }

  bindZoom() {
    const dialog = this.section.querySelector('[data-zoom-dialog]');
    const zoomImage = this.section.querySelector('[data-zoom-image]');
    const viewport = this.section.querySelector('[data-zoom-viewport]');
    const toggle = this.section.querySelector('[data-zoom-toggle]');
    this.section.querySelectorAll('[data-zoom-open]').forEach((trigger) => {
      this.listen(trigger, 'click', () => {
        const image = trigger.querySelector('img');
        if (!image) return;
        this.zoomReturnFocus = trigger;
        this.setGalleryImage(zoomImage, image.dataset.intendedSrc || image.getAttribute('src') || image.src, image.alt);
        viewport.classList.remove('is-zoomed');
        toggle.setAttribute('aria-pressed', 'false');
        toggle.textContent = 'Zoom in';
        dialog.showModal();
        viewport.scrollTo({ left: 0, top: 0, behavior: 'auto' });
      });
    });
    this.listen(toggle, 'click', () => {
      const isZoomed = viewport.classList.toggle('is-zoomed');
      toggle.setAttribute('aria-pressed', String(isZoomed));
      toggle.textContent = isZoomed ? 'Zoom out' : 'Zoom in';
      viewport.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    });
    this.listen(this.section.querySelector('[data-zoom-close]'), 'click', () => dialog.close());
    this.listen(dialog, 'click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    this.listen(dialog, 'close', () => this.zoomReturnFocus?.focus({ preventScroll: true }));
  }

  observeAddButton() {
    const sticky = this.section.querySelector('[data-sticky]');
    this.addObserver = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting;
    }, { threshold: 0.5 });
    this.addObserver.observe(this.form.querySelector('[data-add-button]'));
  }

  cartRoutes() {
    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const root = window.Shopify?.routes?.root || '/';
    return {
      add: routes?.cart_add_url || `${root}cart/add.js`,
      cart: `${routes?.cart_url || `${root}cart`}.js`,
    };
  }

  async fetchCart() {
    const response = await fetch(this.cartRoutes().cart, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Your cart could not be refreshed. Please open your cart to check it.');
    return response.json();
  }

  async addToCart() {
    if (this.busy || !this.selectedVariant?.available) return;
    this.updateQuantity();
    const variant = this.selectedVariant;
    const quantity = this.quantity;
    this.busy = true;
    this.setButtonState();
    this.setStatus('Adding to your cart…');
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter((id, index, ids) => id && ids.indexOf(id) === index);
    const deferred = CartLinesUpdateEvent.createPromise();
    // Avoid an unhandled rejection when a theme does not mount a cart drawer.
    deferred.promise.catch(() => {});
    const detail = {
      source: 'skin-elite-leggings',
      sourceId: this.form.id,
      itemCount: quantity,
      productId: this.section.dataset.productId,
    };
    this.form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variant.id), quantity }],
      promise: deferred.promise,
    }));
    let wasAdded = false;
    try {
      const response = await fetch(this.cartRoutes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity }], sections: sections.join(',') }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Unable to add this item. Please try again.');
      }
      wasAdded = true;
      const cart = await this.fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: payload.sections, didError: false },
      });
      this.setStatus(`${quantity} ${quantity === 1 ? 'item' : 'items'} added to your cart.`);
      if (!document.querySelector('cart-drawer-component')) {
        window.location.assign(typeof Theme !== 'undefined' ? Theme.routes.cart_url : '/cart');
      }
    } catch (error) {
      const message = wasAdded ? 'Item added. Please open your cart to check it before adding again.' : error.message || 'Unable to add this item. Please try again.';
      this.setStatus(message, true);
      this.form.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      try {
        const cart = await this.fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
      } catch (refreshError) {
        deferred.reject(refreshError);
      }
    } finally {
      this.busy = false;
      this.setButtonState();
    }
  }

  destroy() {
    this.events.abort();
    this.addObserver?.disconnect();
    delete this.section.dataset.leggingsInitialized;
  }
}

const instances = new Map();
function initializeLeggings(scope = document) {
  scope.querySelectorAll('[data-leggings-section]').forEach((section) => {
    if (instances.has(section)) return;
    try { instances.set(section, new SkinEliteLeggings(section)); }
    catch (error) { console.error('SKIN ELITE CO. product selector could not initialize:', error); }
  });
}
initializeLeggings();
document.addEventListener('shopify:section:load', (event) => initializeLeggings(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  instances.forEach((instance, section) => {
    if (!event.target.contains(section)) return;
    instance.destroy();
    instances.delete(section);
  });
});
